import type { FeedbackRestore } from "./hooks/useGetFeedback";

/**
 * Route `handle` marker for pages that hydrate from a feedback restore
 * (`location.state.feedbackRestore`, set by the app's RestoreFeedbackPage).
 * Spread it into the route's `handle`; the app layout reads it through
 * {@link routeRestoresFeedbackView} to tell the user when a restore lands on
 * a page that does NOT hydrate — otherwise the restore looks like it did
 * nothing.
 */
export const RESTORES_FEEDBACK_VIEW_HANDLE = {
  restoresFeedbackView: true
} as const;

/** Whether a route `handle` carries {@link RESTORES_FEEDBACK_VIEW_HANDLE}. */
export function routeRestoresFeedbackView(handle: unknown): boolean {
  return (
    typeof handle === "object" &&
    handle !== null &&
    "restoresFeedbackView" in handle &&
    handle.restoresFeedbackView === true
  );
}

/**
 * The feedback restore riding a location's router state
 * (`state.feedbackRestore`, set by the app's RestoreFeedbackPage), or null.
 */
export function readFeedbackRestore(location: {
  state: unknown;
}): FeedbackRestore | null {
  const { state } = location;
  if (typeof state !== "object" || state === null) return null;
  if (!("feedbackRestore" in state)) return null;
  return (state.feedbackRestore as FeedbackRestore | undefined) ?? null;
}

/**
 * Take the restore off the router state by replacing the current entry in
 * place — pathname, search and hash kept — so a refresh or Back/Forward
 * doesn't repeat it. Takes react-router's `navigate` (typed structurally,
 * so this package needs no router dependency).
 */
export function clearFeedbackRestore(
  navigate: (
    to: { pathname: string; search: string; hash: string },
    options: { replace: true; state: null }
  ) => void,
  location: { pathname: string; search: string; hash: string }
): void {
  navigate(
    {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash
    },
    { replace: true, state: null }
  );
}
